import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileText, Mic } from 'lucide-react';
import { requestQueue, type ClientRequest } from '@/lib/requests';
import { cn } from '@/lib/cn';

const TABS: { key: string; label: string }[] = [
  { key: 'new', label: 'Open' },
  { key: 'approved', label: 'Approved' },
  { key: 'converted', label: 'Converted' },
  { key: 'declined', label: 'Declined' },
];

function waited(r: ClientRequest): { text: string; late: boolean } {
  const since = new Date(r.last_activity_at || r.created_at).getTime();
  const h = Math.max(0, Math.round((Date.now() - since) / 3600000));
  return { text: h < 24 ? `${h} h` : `${Math.round(h / 24)} d`, late: h >= 24 };
}

export default function RequestsIntakePage() {
  const [tab, setTab] = useState('new');
  const [kind, setKind] = useState<'' | 'general' | 'creative'>('');
  const q = useQuery({
    queryKey: ['client-requests', tab, kind],
    queryFn: () => requestQueue({ status: tab, ...(kind ? { kind } : {}) }),
    refetchInterval: 30000,
  });

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-muted-strong">Client requests</h1>
        <p className="text-xs text-muted mt-1">
          What clinics ask for from their portal. Talk it through on the request, then approve (a creative request becomes a
          creative on the strategy you pick) or decline with a reason the clinic reads.
        </p>
      </div>

      <div className="flex gap-1.5 flex-wrap items-center">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={cn('px-3 py-1.5 rounded-lg text-xs font-medium transition', tab === t.key ? 'bg-accent-600/15 text-accent-400' : 'text-muted hover:text-muted-strong hover:bg-bg-card')}>
            {t.label}
          </button>
        ))}
        <span className="mx-2 h-4 w-px bg-slate-800" />
        {(['', 'creative', 'general'] as const).map((k) => (
          <button key={k || 'all'} onClick={() => setKind(k)}
            className={cn('px-2.5 py-1 rounded-full text-[11px] border', kind === k ? 'border-accent-400 text-accent-400' : 'border-slate-800 text-muted')}>
            {k === '' ? 'All kinds' : k === 'creative' ? 'Creative' : 'General'}
          </button>
        ))}
      </div>

      {q.isLoading && <div className="text-sm text-muted py-10 text-center">Loading...</div>}
      {q.isError && <div className="text-sm text-muted py-10 text-center">Could not load. <button className="text-accent-400" onClick={() => q.refetch()}>Retry</button></div>}

      <div className="space-y-2.5">
        {(q.data ?? []).map((r) => {
          const w = waited(r);
          const ours = r.status === 'new' && r.waiting_on === 'kaf';
          return (
            <Link key={r.id} to={`/requests/${r.id}`} data-testid="intake-row"
              className={cn('block bg-bg-card border rounded-xl px-4 py-3 hover:border-accent-400/50 transition', ours && w.late ? 'border-amber-400/60' : 'border-bg-card')}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm font-medium text-muted-strong" dir="auto">{r.title}</div>
                <div className="flex items-center gap-2 text-[11px]">
                  {r.client_name && <span className="px-2 py-0.5 rounded-full bg-slate-800 text-muted-strong">{r.client_name}</span>}
                  <span className={cn('px-2 py-0.5 rounded-full', r.kind === 'creative' ? 'bg-accent-600/15 text-accent-400' : 'bg-slate-800 text-muted')}>{r.kind}</span>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap text-[11px] text-muted mt-1.5">
                <span>{r.requested_by_name}</span>
                {r.file_count > 0 && <span className="inline-flex items-center gap-1"><FileText size={11} /> {r.file_count}</span>}
                {r.has_voice && <span className="inline-flex items-center gap-1"><Mic size={11} /> voice</span>}
                {r.status === 'new' && (ours
                  ? <span className={cn(w.late ? 'text-amber-300 font-medium' : '')}>Our turn · waiting {w.text}</span>
                  : <span>Waiting on the clinic</span>)}
                {r.unread > 0 && <span className="px-1.5 rounded-full bg-accent-600 text-white tabular-nums">{r.unread} new</span>}
                {r.status === 'new' && !r.can_decide && <span>(the clinic's SMM decides)</span>}
                <span className="ms-auto">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
            </Link>
          );
        })}
        {q.data && q.data.length === 0 && (
          <div className="border border-dashed border-bg-card rounded-2xl py-14 text-center"><p className="text-xs text-muted">Nothing here.</p></div>
        )}
      </div>
    </div>
  );
}
